import React, { useState, useRef } from 'react';
import SearchBox from '../components/SearchBox';
import LoadingState from '../components/LoadingState';
import ChatBox from '../components/ChatBox';
import JobCard from '../components/JobCard';
import SEO from '../components/SEO';
import { searchJobList, parseSearchText, resetParseContext } from '../services/api';
import './HomePage.css';

const HomePage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchedQuery, setSearchedQuery] = useState('');
  const [chatMode, setChatMode] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [query, setQuery] = useState('');
  const [cvFile, setCvFile] = useState(null);
  const userTextsRef = useRef([]);
  const cvFileRef = useRef(null);

  const performSearch = async (params) => {
    setIsLoading(true);
    setChatMode(false);
    setChatMessages(prev => prev.filter(m => !m.typing));

    try {
      const response = await searchJobList(params);
      await new Promise(resolve => setTimeout(resolve, 2500));
      const jobs = response.items || [];
      setResults(jobs.length > 0 ? jobs : []);
    } catch {
      await new Promise(resolve => setTimeout(resolve, 2500));
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  };

  // API'den dönen parse sonucunu işle
  const handleParseResult = async (parsed, userQuery) => {
    const data = parsed.data || parsed;
    const isReady = data.isReady === true;
    const aiMessage = parsed.clarifyQuestion || data.message || (!parsed.status && parsed.message) || null;

    // isReady false + AI mesaj var → ChatBox aç
    if (!isReady && aiMessage) {
      setChatMode(true);

      if (chatMessages.length === 0 && userQuery) {
        setChatMessages([
          { role: 'user', text: userQuery },
          { role: 'ai', text: aiMessage },
        ]);
      } else {
        setChatMessages(prev => [
          ...prev.filter(m => !m.typing),
          { role: 'ai', text: aiMessage },
        ]);
      }
      return;
    }

    // isReady=true veya arama verisi hazır → SearchJobList'e gönder
    const searchParams = {
      searchText: data.searchText ?? null,
      title: data.title ?? null,
      currentLocation: data.currentLocation ?? data.location ?? null,
      workingType: data.workingType ?? null,
      experienceYear: data.experienceYear ?? null,
      educationLevelId: data.educationLevelId ?? null,
      isMillitaryCompleted: data.isMillitaryCompleted ?? null,
      age: data.age ?? null,
    };

    const fullQuery = userTextsRef.current.join(', ');
    setSearchedQuery(fullQuery);
    setQuery(fullQuery);

    await performSearch(searchParams);
  };

  const handleSearch = async ({ query: q, cvFile: cv }) => {
    setResults(null);
    setChatMode(false);
    setChatMessages([]);
    setSearchedQuery(q);
    userTextsRef.current = [q];
    cvFileRef.current = cv || null;
    resetParseContext();

    // Kullanıcının yazdığını + CV'yi ParseSearchText API'sine gönder
    try {
      const parsed = await parseSearchText(q, cv);
      await handleParseResult(parsed, q);
    } catch {
      // API başarısız olursa direkt arama yap
      const searchParams = { searchText: q };
      setSearchedQuery(q);
      await performSearch(searchParams);
    }
  };

  const handleChatReply = async (message) => {
    setChatMessages(prev => [...prev, { role: 'user', text: message }]);
    userTextsRef.current.push(message);

    // Typing göster
    setChatMessages(prev => [...prev, { role: 'ai', text: '', typing: true }]);

    try {
      // Tüm kullanıcı mesajlarını virgülle ayırarak gönder
      const fullText = userTextsRef.current.join(', ');
      const parsed = await parseSearchText(fullText, cvFileRef.current);
      await handleParseResult(parsed, null);
    } catch {
      setChatMessages(prev => [
        ...prev.filter(m => !m.typing),
        { role: 'ai', text: 'Bir hata oluştu. Lütfen tekrar deneyin.' },
      ]);
    }
  };

  const handleChatClose = () => {
    setChatMode(false);
  };

  return (
    <div className="home">
      <SEO
        title="Yapay Zeka ile İş Bul"
        description="Yapay zeka ile sana en uygun iş ilanlarını bul. CV'ni yükle, yapay zeka analiz etsin ve en uygun pozisyonları sana eşleştirsin. Ücretsiz iş arama platformu."
        path="/"
      />
      <section className={`hero ${results || isLoading ? 'hero--compact' : ''}`}>
        <div className="hero__bg"></div>

        <div className="hero__container">
          {!results && !isLoading && (
            <>
              <h1 className="hero__title">
                Yapay Zeka{' '}
                <span className="hero__title-gradient">işBulsun</span>
                <span className="hero__title-domain">.com</span>
              </h1>

              <p className="hero__subtitle">
                Nasıl bir iş aradığını, bulunduğun ili ve tecrübeni yaz sana uygun işleri Yapay Zeka bulsun.
              </p>
            </>
          )}

          <SearchBox
            onSearch={handleSearch}
            isLoading={isLoading}
            query={query}
            onQueryChange={setQuery}
            cvFile={cvFile}
            onCvChange={setCvFile}
          />

          {isLoading && <LoadingState />}
        </div>
      </section>

      {chatMode && (
        <ChatBox
          messages={chatMessages}
          onSend={handleChatReply}
          onClose={handleChatClose}
        />
      )}

      {results && !isLoading && (
        <section className="results">
          <div className="results__container">
            <div className="results__header">
              <p className="results__count">
                <strong>{results.length}</strong> ilan bulundu
                {searchedQuery && <span className="results__query"> — "{searchedQuery}"</span>}
              </p>
            </div>

            {results.length > 0 ? (
              <div className="results__grid">
                {results.map((job, index) => (
                  <JobCard key={job.id || index} job={job} />
                ))}
              </div>
            ) : (
              <div className="results__empty">
                <p>Aramanızla eşleşen ilan bulunamadı. Farklı terimlerle tekrar deneyin.</p>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
